import { useQuery } from "react-query";
import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";

import { WellDetailCardData, getWellDetailCard } from "api/data";

import { EntityKind } from "models/entityKind";

import { WELL_CARD_PANEL_FIELDS } from "../constants";
import { useWellCardForecastQualification } from "./useWellCardForecastQualification";

export interface WellCardPanelFieldsResult {
  data: Maybe<WellDetailCardData>;
  isLoading: boolean;
  isError: boolean;
  /** `data` still describes the previous target while the new one loads. */
  isPreviousData: boolean;
  refetch: () => void;
}

/**
 * One request for every field-backed block in the panel. The endpoint ignores
 * `group` and answers with a flat dictionary keyed by the dotted property
 * name, so a hand-picked list across eight catalogue groups costs one call.
 */
export function useWellCardPanelFields(uwi: Maybe<string>): WellCardPanelFieldsResult {
  const { defaultSignificantFigures, useStrictRounding } = useSelector(
    (state: RootState) => state.app.significantFiguresSettings
  );

  const { fields, forecastSource, forecastTrumpingSettings, toBaseNames } =
    useWellCardForecastQualification(WELL_CARD_PANEL_FIELDS);

  const { data, isLoading, isError, isPreviousData, refetch } = useQuery(
    [
      "wellCardPanelFields",
      uwi,
      defaultSignificantFigures,
      useStrictRounding,
      forecastSource,
      forecastTrumpingSettings
    ],
    async () => {
      const response = await getWellDetailCard({
        uwid: uwi,
        fields,
        entityKind: EntityKind.Well,
        defaultSignificantFigures,
        useStrictRounding,
        forecastTrumpingSettings
      });
      return toBaseNames(response.data as WellDetailCardData);
    },
    {
      enabled: !!uwi,
      retry: false,
      // The key carries the uwi, so without this the panel blanks on every
      // retarget until the next response lands.
      keepPreviousData: true
    }
  );

  return { data, isLoading, isError, isPreviousData, refetch };
}
